import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { MessageService } from 'primeng/api';
import { SignerService, AssinanteRequestDTO, AssinanteResponseDTO } from '../../services/signer.service';

@Component({
    selector: 'app-signer-form',
    standalone: true,
    imports: [
        CommonModule, 
        FormsModule, 
        ReactiveFormsModule,
        CardModule,
        ButtonModule,
        InputTextModule,
        ToggleSwitchModule,
        SelectModule,
        ToastModule,
        ProgressSpinnerModule,
        DividerModule
    ],
    providers: [MessageService],
    template: `
        <p-card>
            <ng-template pTemplate="header">
                <div class="flex justify-content-between align-items-center p-3">
                    <h2 class="m-0">{{ isEditMode ? 'Editar Assinante' : 'Novo Assinante' }}</h2>
                    <p-button 
                        label="Voltar" 
                        icon="pi pi-arrow-left" 
                        (onClick)="goBack()"
                        severity="secondary"
                        [outlined]="true">
                    </p-button>
                </div>
            </ng-template>

            <ng-template pTemplate="content">
                <div class="p-4">
                    <form [formGroup]="signerForm" (ngSubmit)="onSubmit()">
                        <!-- Nome do Assinante -->
                        <div class="field">
                            <label for="nomeAssinante" class="block text-900 font-medium mb-2">
                                Nome do Assinante *
                            </label>
                            <span class="p-input-icon-right w-full">
                                <input 
                                    pInputText 
                                    type="text" 
                                    id="nomeAssinante"
                                    formControlName="nomeAssinante"
                                    placeholder="Digite o nome completo do assinante"
                                    class="w-full"
                                    [class.ng-invalid]="nomeAssinante?.invalid && (nomeAssinante?.dirty || nomeAssinante?.touched)"
                                    [class.ng-dirty]="nomeAssinante?.dirty">
                                <i class="pi pi-user"></i>
                            </span>
                            <small 
                                *ngIf="nomeAssinante?.invalid && (nomeAssinante?.dirty || nomeAssinante?.touched)" 
                                class="p-error block mt-1">
                                <span *ngIf="nomeAssinante?.errors?.['required']">Nome é obrigatório</span>
                                <span *ngIf="nomeAssinante?.errors?.['minlength']">Nome deve ter pelo menos 2 caracteres</span>
                            </small>
                        </div>

                        <!-- Função do Assinante -->
                        <div class="field">
                            <label for="funcaoAssinante" class="block text-900 font-medium mb-2">
                                Função do Assinante *
                            </label>
                            <span class="p-input-icon-right w-full">
                                <input 
                                    pInputText 
                                    type="text" 
                                    id="funcaoAssinante"
                                    formControlName="funcaoAssinante"
                                    placeholder="Digite a função/cargo do assinante"
                                    class="w-full"
                                    [class.ng-invalid]="funcaoAssinante?.invalid && (funcaoAssinante?.dirty || funcaoAssinante?.touched)"
                                    [class.ng-dirty]="funcaoAssinante?.dirty">
                                <i class="pi pi-briefcase"></i>
                            </span>
                            <small 
                                *ngIf="funcaoAssinante?.invalid && (funcaoAssinante?.dirty || funcaoAssinante?.touched)" 
                                class="p-error block mt-1">
                                <span *ngIf="funcaoAssinante?.errors?.['required']">Função é obrigatória</span>
                                <span *ngIf="funcaoAssinante?.errors?.['minlength']">Função deve ter pelo menos 2 caracteres</span>
                            </small>
                        </div>

                        <!-- Sequência do Assinante -->
                        <div class="field">
                            <label for="sequence" class="block text-900 font-medium mb-2">
                                Sequência do Assinante *
                            </label>
                            <span class="p-input-icon-right w-full">
                                <input 
                                    pInputText 
                                    type="number" 
                                    id="sequenciaAssinante"
                                    formControlName="sequenciaAssinante"
                                    placeholder="Digite a sequência do assinante"
                                    class="w-full"
                                    [class.ng-invalid]="signerForm.get('sequenciaAssinante')?.invalid && (signerForm.get('sequenciaAssinante')?.dirty || signerForm.get('sequenciaAssinante')?.touched)"
                                    [class.ng-dirty]="signerForm.get('sequence')?.dirty">
                                <i class="pi pi-sort-numeric-up"></i>
                            </span>
                            <small 
                                *ngIf="signerForm.get('sequenciaAssinante')?.invalid && (signerForm.get('sequenciaAssinante')?.dirty || signerForm.get('sequenciaAssinante')?.touched)" 
                                class="p-error block mt-1">
                                <span *ngIf="signerForm.get('sequenciaAssinante')?.errors?.['required']">Sequência é obrigatória</span>
                                <span *ngIf="signerForm.get('sequenciaAssinante')?.errors?.['min']">Sequência deve ser um número positivo</span>
                            </small>
                        </div>


                        <!-- Status Ativo -->
                        <div class="field" *ngIf="isEditMode">
                            <div class="flex align-items-center gap-3">
                                <label for="statusAssinante" class="text-900 font-medium">
                                    Status Ativo
                                </label>
                                <p-toggleSwitch 
                                    id="statusAssinante"
                                    formControlName="statusAssinante"
                                    [trueValue]="true"
                                    [falseValue]="false">
                                </p-toggleSwitch>
                                <span class="text-600 text-sm">
                                    {{ signerForm.get('statusAssinante')?.value ? 'Ativo' : 'Inativo' }}
                                </span>
                            </div>
                        </div>

                        <p-divider></p-divider>

                        <!-- Botões de Ação -->
                        <div class="flex justify-content-end gap-2 pt-3">
                            <p-button 
                                label="Cancelar" 
                                icon="pi pi-times" 
                                (onClick)="goBack()"
                                severity="secondary"
                                [outlined]="true">
                            </p-button>
                            
                            <p-button 
                                [label]="isEditMode ? 'Atualizar' : 'Criar'" 
                                [icon]="isEditMode ? 'pi pi-check' : 'pi pi-plus'" 
                                type="submit"
                                [disabled]="signerForm.invalid || isLoading"
                                [loading]="isLoading"
                                severity="primary">
                            </p-button>
                        </div>
                    </form>
                </div>
            </ng-template>
        </p-card>
        
        <p-toast></p-toast>
    `,
    styles: [`
        :host ::ng-deep .p-card .p-card-header {
            background: var(--surface-50);
            border-bottom: 1px solid var(--surface-200);
        }

        :host ::ng-deep .p-card .p-card-content {
            padding: 0;
        }

        .field {
            margin-bottom: 1.5rem;
        }

        .field:last-child {
            margin-bottom: 0;
        }

        :host ::ng-deep .p-inputtext.ng-invalid.ng-dirty {
            border-color: var(--red-500);
        }

        :host ::ng-deep .p-button.p-button-loading {
            opacity: 0.6;
        }

        @media (max-width: 768px) {
            .flex.justify-content-end {
                flex-direction: column;
            }
            
            .flex.justify-content-end .p-button {
                width: 100%;
                margin-bottom: 0.5rem;
            }
            
            .flex.justify-content-end .p-button:last-child {
                margin-bottom: 0;
            }
        }
    `]
})
export class SignerFormComponent implements OnInit {
    signerForm!: FormGroup;
    isEditMode = false;
    signerId: number | null = null;
    isLoading = false;

    constructor(
        private fb: FormBuilder,
        private signerService: SignerService,
        private router: Router,
        private route: ActivatedRoute,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.createForm();
        this.checkEditMode();
    }

    private createForm(): void {
        this.signerForm = this.fb.group({
            nomeAssinante: ['', [Validators.required, Validators.minLength(2)]],
            funcaoAssinante: ['', [Validators.required, Validators.minLength(2)]],
            sequenciaAssinante: [0, [Validators.required, Validators.min(0)]],
            statusAssinante: [true]
        });
    }

    private checkEditMode(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            this.isEditMode = true;
            this.signerId = Number(id);
            this.loadSigner(this.signerId);
        }
    }

    private loadSigner(id: number): void {
        this.isLoading = true;
        this.signerService.obterPorId(id).subscribe({
            next: (signer: AssinanteResponseDTO) => {
                this.signerForm.patchValue({
                    nomeAssinante: signer.nomeAssinante,
                    funcaoAssinante: signer.funcaoAssinante,
                    sequenciaAssinante: signer.sequenciaAssinante,
                    statusAssinante: signer.statusAssinante
                });
                this.isLoading = false;
            },
            error: (error: any) => {
                console.error('❌ Erro ao carregar assinante:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao carregar dados do assinante'
                });
                this.isLoading = false;
            }
        });
    }

    onSubmit(): void {
        if (this.signerForm.valid && !this.isLoading) {
            this.isLoading = true;
            const formData = this.signerForm.value;
            
            //console.log('📝 Dados do formulário:', formData);

            if (this.isEditMode && this.signerId) {
                this.updateSigner(formData);
            } else {
                this.createSigner(formData);
            }
        } else {
            this.markFormGroupTouched();
        }
    }

    private createSigner(data: AssinanteRequestDTO): void {
        this.signerService.criar(data).subscribe({
            next: (response) => {
                //console.log('✅ Assinante criado com sucesso:', response);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: 'Assinante criado com sucesso'
                });
                this.isLoading = false;
                this.router.navigate(['/home/signers']);
            },
            error: (error) => {
                console.error('❌ Erro ao criar assinante:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao criar assinante'
                });
                this.isLoading = false;
            }
        });
    }

    private updateSigner(data: AssinanteRequestDTO): void {
        if (!this.signerId) return;

        this.signerService.alterar(this.signerId, data).subscribe({
            next: (response) => {
                //console.log('✅ Assinante atualizado com sucesso:', response);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: 'Assinante atualizado com sucesso'
                });
                this.isLoading = false;
                this.router.navigate(['/home/signers']);
            },
            error: (error) => {
                console.error('❌ Erro ao atualizar assinante:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao atualizar assinante'
                });
                this.isLoading = false;
            }
        });
    }

    private markFormGroupTouched(): void {
        Object.keys(this.signerForm.controls).forEach(key => {
            this.signerForm.get(key)?.markAsTouched();
        });
    }

    goBack(): void {
        this.router.navigate(['/home/signers']);
    }

    // Getters para facilitar acesso aos controles do formulário
    get nomeAssinante() { return this.signerForm.get('nomeAssinante'); }
    get funcaoAssinante() { return this.signerForm.get('funcaoAssinante'); }
    get sequenciaAssinante() { return this.signerForm.get('sequenciaAssinante'); }
    get statusAssinante() { return this.signerForm.get('statusAssinante'); }
}